import { useEffect, useState } from "react";
import { useDispatch } from "react-redux"
import { Combobox, EInputTypes, ETypeOfNotification, getDate, InputDate, InputTextCustom, Modal, setNotification, useFormValidation } from "medere-ui-framework";
import CustomCheckBox from "../../components/CustomCheckBox/CustomCheckBox";
import { Button } from "primereact/button";
import '../Recipe/PatientAgenda/Views/CreatePatientView.scss';
import { defaultHealthPatientCoverageDTO, HealthPatientCoverageDTO } from "../../dto/HealthPatientCoverageDTO";
import { ComboDataDTO } from "../../dto/ComboDataDTO";
import { HealthEntityPlanDTO } from "../../dto/HealthEntityPlanDTO";
import { HttpStatusCode } from "axios";
import "./HealthPatientCoverageModal.scss";
import { useHealthPatientService } from "../../servicesHooks/HealthPatientService/useHealthPatientService";
import { useEntityService } from "../../servicesHooks/Entity/useEntityService";

interface HealthPatientCoverageModalProps {
    header: string | React.ReactNode;
    defaultCoverage: HealthPatientCoverageDTO,
    openHealthPatientCoverageModal: boolean;
    setOpenHealthPatientCoverageModal: (open: boolean) => void;
}

export default function HealthPatientCoverageModal({ header, defaultCoverage, openHealthPatientCoverageModal, setOpenHealthPatientCoverageModal }: HealthPatientCoverageModalProps) {
    const { saveHealthPatientCoverage } = useHealthPatientService();
    const { getHealthEntityComboDataDTO, getHealthEntityPlanDTO } = useEntityService();

    const dispatch = useDispatch();
    const [coverage, setCoverage] = useState<HealthPatientCoverageDTO>(defaultCoverage);
    const { errors: formErrors, handleValidation } = useFormValidation(coverage);


    const handleHealthPatientCoverageModalSave = (coverage: HealthPatientCoverageDTO, withValidation: boolean) => {
        if (withValidation) {
            handleValidation(() => {
                handleHealthPatientCoverageModalSave(coverage, false);
            }, undefined, ["healthpatientcoverage", "coverageend"],
                () => {
                    console.log(`FormErrors: ${JSON.stringify(formErrors)}`);
                    dispatch(setNotification({ title: "Error al guardar los datos", desc: "Por favor verifque todos los campos", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }))
                });
            return;
        }
        saveHealthPatientCoverage(coverage)?.then(res => {
            if (res.status == HttpStatusCode.Ok) {
                dispatch(setNotification({ title: "Cobertura añadida", icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }))
                setOpenHealthPatientCoverageModal(false);
                return;
            }
        }).catch(err => {
            dispatch(setNotification({ title: "Error al intentar guardar la cobertura para el paciente", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }))
        });;
    };

    const handleHealthPatientCoverageModalCancel = () => {
        setOpenHealthPatientCoverageModal(false);
    };

    useEffect(() => {
        console.log("Cambio de defaultCoverage");
        setCoverage(prev =>
            ({ ...prev, healthentity: defaultCoverage.healthentity ?? prev.healthentity, healthentitydesc: defaultCoverage.healthentitydesc ?? prev.healthentitydesc, healthentityplan: defaultCoverage.healthentityplan ?? prev.healthentityplan, healthentityplandesc: defaultCoverage.healthentityplandesc ?? prev.healthentityplandesc, patient: defaultCoverage.patient ?? prev.patient, patientdesc: defaultCoverage.patientdesc ?? prev.patientdesc, affiliatenumber: prev.affiliatenumber ?? defaultCoverage.affiliatenumber })
        );
    }, [defaultCoverage]);


    return (
        <Modal
            visible={openHealthPatientCoverageModal}
            setVisible={setOpenHealthPatientCoverageModal}
            header={header}
            headerStyle={{ fontSize: '12px' }}
            className="h-auto"
        >
            <div className="flex flex-column hpcmodal-container ">
                <Combobox label={"Obra social"}
                    className={`w-full`}
                    getItems={getHealthEntityComboDataDTO}
                    optionLabel='name'
                    placeholder="Seleccionar"
                    value={coverage.healthentitydesc}
                    setValue={(e: ComboDataDTO) => {
                        if (e) {
                            setCoverage(
                                prev => (
                                    { ...prev, healthentity: e.id, healthentitydesc: e.name, healthentityplan: -1, healthentityplandesc: '' }
                                )
                            )
                        }
                    }}
                    error={formErrors.healthentity}
                />

                {(coverage.healthentity !== -1 && coverage.healthentitydesc != "") &&
                    <Combobox label={"Plan"}
                        className="w-full"
                        getItems={getHealthEntityPlanDTO}
                        params={{ healthentity: coverage.healthentity }}
                        optionLabel='name'
                        value={coverage.healthentityplandesc}
                        setValue={(e: HealthEntityPlanDTO) => {
                            if (e) {
                                setCoverage(prev => ({
                                    ...prev,
                                    healthentityplan: e.healthentityplan ?? -1, healthentityplandesc: e.name
                                }));
                            }
                        }}
                        reLoadItemsValue={coverage.healthentity}
                        error={formErrors.healthentityplan}
                        allowsEmptyValues={false}
                    />}
                <InputTextCustom label={"Nro. de afiliado"}
                    className="w-full"
                    value={coverage.affiliatenumber}
                    inputType={EInputTypes.NUMBER}
                    onChange={(e: any) => {
                        setCoverage(prev => ({ ...prev, affiliatenumber: e.target.value }))
                    }}
                    useGrouping={false}
                    error={formErrors.affiliatenumber}
                />
                <InputDate label={"Inicio de cobertura"}
                    className="w-full"
                    value={getDate(coverage.coveragestart)}
                    error={formErrors.coveragestart}
                    onChange={(e: any) =>
                        setCoverage({ ...coverage, coveragestart: e.value })
                    }
                />
                <InputDate label={"Fin de la cobertura"}
                    className="w-full"
                    value={getDate(coverage.coverageend)}
                    error={formErrors.coverageend}
                    onChange={(e: any) =>
                        setCoverage({ ...coverage, coverageend: e.value })
                    }
                />
                <div className="flex flex-column my-2">
                    <label style={{ marginBottom: '0.5rem' }}>
                        <CustomCheckBox
                            checked={coverage.primarycoverage}
                            onChange={(e) => setCoverage(prev => ({ ...prev, primarycoverage: e.target.checked ?? false }))}
                        />
                        <span style={{ fontSize: '15px' }}>Usar esta Obra social como favorita.</span>
                    </label>
                    <label>
                        <CustomCheckBox
                            checked={coverage.voluntary}
                            onChange={(e) => setCoverage(prev => ({ ...prev, voluntary: e.target.checked ?? false }))}
                        />
                        <span style={{ fontSize: '15px' }}>La cobertura es a través de su trabajo</span>
                    </label>
                </div>
                <div className="flex flex-column mt-4">
                    <Button
                        className='btnRpContinue'
                        label={'Agregar cobertura'}
                        onClick={() => { handleHealthPatientCoverageModalSave(coverage, true) }}
                        style={{ marginBottom: '15px', backgroundColor: '#14C7BC', border: 'none', height: '36px' }}
                    />
                    <Button
                        className='btnRpBack'
                        label="Cancelar"
                        onClick={handleHealthPatientCoverageModalCancel}
                        style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA' }}
                    />
                </div>
            </div>
        </Modal>
    );
}
